import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { createSyncSystem, type SyncSystem } from './create-sync-system';
export type { SyncSystem };
import { LocalPersistence } from './local-persistence';
import { NoOpPersistence } from './no-op-persistence';
import {
  fireLocalReady,
  fireAuthError,
  fireWorkspaceMembershipChanged,
  type WorkspaceMembershipChange,
} from './sync-event-bus';
import { fireDocAccessDenied, type AccessDeniedReason } from './doc-access-denied-bus';
import { handleEntityEvent } from './entity-event-handler';
import { useEntityStore, setActiveEntityStore } from './entity-store';
import type { OpResult, EntityEventPayload } from './entity-store';
import { entityProjection } from './entity-projection';
import { decodeJson } from './envelope';
import {
  KIND_AUTH_OK,
  KIND_AUTH_ERROR,
  KIND_SUBSCRIBE_OK,
  KIND_UNSUBSCRIBE_OK,
  KIND_OP_RESULT,
  KIND_SYNC_RESPONSE,
  KIND_SYNC_EVENT,
  KIND_ERROR,
  KIND_WORKSPACE_MEMBERSHIP_CHANGED,
  KIND_YJS_SYNC_STEP1,
  KIND_YJS_AWARENESS,
  KIND_AGENT_RUN,
  KIND_AGENT_END,
  kindName,
} from './envelope-kinds';

// Bounds come from the generated constants: an earlier hardcoded range
// drifted out of sync with the protocol and warned on every agent frame.
export function isEngineHandledKind(kind: number): boolean {
  return (
    (kind >= KIND_YJS_SYNC_STEP1 && kind <= KIND_YJS_AWARENESS) ||
    (kind >= KIND_AGENT_RUN && kind <= KIND_AGENT_END)
  );
}

export type SyncStatus = 'connected' | 'connecting' | 'disconnected';

export type SyncMode = 'normal' | 'share';

// ---------------------------------------------------------------------------
// SyncSystem context
// ---------------------------------------------------------------------------

const SyncSystemCtx = createContext<SyncSystem | null>(null);
export { SyncSystemCtx as SyncSystemContext };
const SyncModeCtx = createContext<SyncMode>('normal');

export function useSyncSystem(): SyncSystem {
  const sys = useContext(SyncSystemCtx);
  if (!sys) throw new Error('useSyncSystem must be used inside SyncSystemProvider');
  return sys;
}

/**
 * Non-throwing check for whether a SyncSystemProvider is mounted above. Use to
 * gate sync-dependent subtrees (which call `useSyncSystem()` and would throw)
 * when the provider mounts conditionally — e.g. the recording HUD, where the
 * provider waits on auth/workspace readiness while the chrome renders eagerly.
 */
export function useHasSyncSystem(): boolean {
  return useContext(SyncSystemCtx) !== null;
}

export function useSyncTransport() {
  return useSyncSystem().transport;
}

export function useYjsRegistry() {
  return useSyncSystem().registry;
}

export function useSyncEngine() {
  return useSyncSystem().engine;
}

export function useSyncMode(): SyncMode {
  return useContext(SyncModeCtx);
}

export function useIsShareMode(): boolean {
  return useContext(SyncModeCtx) === 'share';
}

export function useSyncStatus(): SyncStatus {
  const sys = useSyncSystem();
  const [status, setStatus] = useState<SyncStatus>(() => {
    const s = sys.transport.getStatus();
    return s === 'connected' ? 'connected' : s === 'connecting' ? 'connecting' : 'disconnected';
  });

  useEffect(() => {
    return sys.transport.onStatus((s) => {
      const mapped: SyncStatus =
        s === 'connected' ? 'connected' :
        s === 'connecting' ? 'connecting' :
        'disconnected';
      setStatus(mapped);

      if (s === 'auth-error') {
        fireAuthError('Authentication failed');
      }
    });
  }, [sys]);

  return status;
}

// ---------------------------------------------------------------------------
// SyncSystemProvider
// ---------------------------------------------------------------------------

interface SyncSystemProviderProps {
  /**
   * The workspace this provider is scoped to. The provider activates the
   * entity store for this workspace BEFORE constructing the sync system,
   * so opSyncBridge and other subscribers attach to the correct store.
   * Key the component on this prop (`<SyncSystemProvider key={id} ...>`)
   * to force a clean remount on workspace switch.
   */
  workspaceId: string;
  wsUrl: string;
  /**
   * Sync mode selects the persistence adapter at construction time
   * (design decision #9 — DI, no runtime flags):
   *   - 'normal' (default): LocalPersistence → IndexedDB
   *   - 'share': NoOpPersistence → ephemeral, server-only
   */
  mode?: SyncMode;
  children: React.ReactNode;
}

export function SyncSystemProvider({ workspaceId, wsUrl, mode = 'normal', children }: SyncSystemProviderProps) {
  // Activate the entity store for this workspace, then construct the system.
  // The activation must happen synchronously inside the useMemo BEFORE
  // createSyncSystem() runs, because createSyncSystem → startOpSync subscribes
  // to useEntityStore, which would throw if no store is active.
  // workspaceId is included in deps so a workspace switch (parent rebinds the
  // prop, ideally via `key={workspaceId}` for a full remount) rebuilds the
  // system against the new store.
  // Own the sync system in a ref, NOT useMemo. React's StrictMode (dev) and any
  // remount run the dispose effect's cleanup, which tears the system down —
  // closing its WebSocket. useMemo would then hand back the SAME now-disposed
  // object on the remount (deps unchanged) → consumers bind to a dead sync
  // system and are stuck loading forever (the recording-HUD endless-loading
  // bug). Instead, recreate during render whenever the current system is missing
  // or disposed, or when workspace/url/mode changes. The cleanup nulls the ref
  // and forces a re-render so the next render builds a fresh, live system.
  const systemRef = useRef<SyncSystem | null>(null);
  const depsRef = useRef<string>('');
  const depsKey = `${workspaceId}|${wsUrl}|${mode}`;
  const [, forceRecreate] = useState(0);

  if (
    systemRef.current === null ||
    systemRef.current.disposed ||
    depsRef.current !== depsKey
  ) {
    // Tear down a stale-but-not-disposed system from a deps change before
    // replacing it (disposed ones are already torn down).
    if (systemRef.current && !systemRef.current.disposed) {
      void systemRef.current.dispose();
    }
    setActiveEntityStore(workspaceId);
    const persistence = mode === 'share' ? new NoOpPersistence() : new LocalPersistence();
    const readOnly = mode === 'share';
    systemRef.current = createSyncSystem(persistence, {
      readOnly,
      onLocalReady: (id) => fireLocalReady(id),
    });
    depsRef.current = depsKey;
  }
  const system = systemRef.current;

  // Wire envelope frame handler for authOk, opResult, syncResponse, auth-error,
  // syncEvent, error. The op-sync bridge handle lives on system.opSync (created
  // in createSyncSystem) so services can also reach sendEphemeralBatch via
  // useSyncSystem().
  useEffect(() => {
    // Bootstrap-race buffer (F2). When the client sends sendSync(null), the
    // server-side bootstrap snapshot may take long enough that live updates
    // for already-subscribed docs arrive over the same socket FIRST — those
    // KIND_SYNC_EVENT frames carry newer state than the bootstrap snapshot
    // that's still being prepared. If we apply them eagerly then wipe-and-
    // apply the (older) bootstrap, the newer state is lost. Buffer live
    // events while a bootstrap is in flight and drain them AFTER the
    // bootstrap applies so newer-than-bootstrap data wins.
    let bootstrapPending = false;
    const liveEventBuffer: Record<string, unknown>[] = [];

    const unsub = system.transport.onFrame((envelope) => {
      switch (envelope.kind) {
        case KIND_AUTH_OK: {
          system.transport.setAuthenticated();
          const cursor = useEntityStore.getState().cursor;
          // The initial post-auth sync without a cursor is a bootstrap — flag
          // it so KIND_SYNC_EVENT frames buffer until the response arrives.
          if (cursor === null) bootstrapPending = true;
          void system.engine.sendSync(cursor).catch((err) => {
            console.warn('[SyncSystemProvider] initial sync failed:', err);
          });
          break;
        }

        case KIND_AUTH_ERROR: {
          const msg = envelope.json.length > 0
            ? decodeJson<Record<string, unknown>>(envelope.json)
            : {};
          fireAuthError((msg.message as string) ?? 'Authentication failed');
          void system.transport.disconnect();
          break;
        }

        case KIND_SUBSCRIBE_OK: {
          system.engine.resolveSubscribe(envelope.corrId);
          break;
        }

        case KIND_UNSUBSCRIBE_OK: {
          system.engine.resolveUnsubscribe(envelope.corrId);
          break;
        }

        case KIND_OP_RESULT: {
          const msg = decodeJson<{ results?: OpResult[] }>(envelope.json);
          const results = msg.results ?? [];
          // opSyncBridge owns both the persistent and ephemeral op correlation
          // maps. resolveBatch tries ephemeral first, falls back to persistent.
          system.opSync.resolveBatch(envelope.corrId, results);
          break;
        }

        case KIND_SYNC_RESPONSE: {
          // Resolve the pending sync promise (if any) before applying results
          // so awaited callers see the completion in-order with the effects.
          system.engine.resolveSync(envelope.corrId);

          const msg = decodeJson<{
            results?: EntityEventPayload[];
            cursor?: string | null;
            hasNextPage?: boolean;
            isBootstrap?: boolean;
          }>(envelope.json);
          const results = msg.results ?? [];
          const cursor = msg.cursor ?? null;
          const hasNextPage = msg.hasNextPage ?? false;
          const isBootstrap = msg.isBootstrap ?? false;

          if (cursor || isBootstrap) {
            if (isBootstrap) {
              // Atomic wipe-and-apply in a single store transition. The old
              // path called resetEntities() then applyBatch() — two separate
              // set() calls that surfaced a transient empty state to every
              // subscriber and let racing KIND_SYNC_EVENT frames apply
              // against a half-populated store. resetAndApplyBatch performs
              // both in one set() and matches resetEntities' "preserve
              // cursor + pendingOps until applyBatch overwrites cursor"
              // contract.
              entityProjection.notifyBootstrapStart();
              if (cursor) {
                useEntityStore.getState().resetAndApplyBatch(results, cursor);
              } else {
                useEntityStore.getState().resetEntities();
              }
              useEntityStore.getState().reapplyPendingOps();
              entityProjection.notifyBootstrapEnd();

              // Drain any live KIND_SYNC_EVENT frames that arrived while
              // the bootstrap was in flight. They carry newer state than
              // the snapshot, so they must apply ON TOP of bootstrap data.
              bootstrapPending = false;
              if (liveEventBuffer.length > 0) {
                const drained = liveEventBuffer.splice(0, liveEventBuffer.length);
                for (const buffered of drained) {
                  handleEntityEvent(buffered);
                }
              }
            } else if (cursor) {
              useEntityStore.getState().applyBatch(results, cursor);
            }
          }

          if (hasNextPage && cursor) {
            void system.engine.sendSync(cursor).catch((err) => {
              console.warn('[SyncSystemProvider] catch-up sync failed:', err);
            });
          }
          break;
        }

        case KIND_SYNC_EVENT: {
          const payload = decodeJson<Record<string, unknown>>(envelope.json);
          if (bootstrapPending) {
            // Defer until the bootstrap response arrives and the snapshot
            // is applied — see the bootstrap-race buffer comment above.
            liveEventBuffer.push(payload);
          } else {
            handleEntityEvent(payload);
          }
          break;
        }

        case KIND_WORKSPACE_MEMBERSHIP_CHANGED: {
          // Server pushed a workspace-membership state change for this user.
          // Forward via the event bus; NulaApp listens and either kicks the
          // user out of the workspace (removed) or refreshes the workspace
          // store (role / default-permission change).
          const payload = decodeJson<WorkspaceMembershipChange>(envelope.json);
          fireWorkspaceMembershipChanged(payload);
          break;
        }

        case KIND_ERROR: {
          const msg = decodeJson<Record<string, unknown>>(envelope.json);
          const code = (msg.code as string) ?? 'unknown';
          const message = (msg.message as string) ?? '';

          // Correlated error — route to the pending request that owns the corrId.
          // If a pending request matched, the caller is responsible for surfacing
          // the failure (e.g. createSyncSystem fires docAccessDenied for forbidden).
          const matched = system.engine.rejectByCorrId(envelope.corrId, code, message);
          if (matched) break;

          // Replayed-subscribe corrIds aren't in any pending-request map (replay
          // is fire-and-forget), so rejectByCorrId returns false even when the
          // error is genuinely for one of those subscribes. The engine's replay
          // map keeps the corrId → entityId mapping for exactly this case.
          if (code === 'forbidden' || code === 'permission_denied') {
            const replayedEntityId = system.engine.takeReplayedSubscribeEntityId(envelope.corrId);
            if (replayedEntityId) {
              fireDocAccessDenied(replayedEntityId, code as AccessDeniedReason);
              system.registry.unsubscribe(replayedEntityId);
              break;
            }
          }

          // A Yjs write refused because the user may only read this doc. The
          // doc stays open; the refused change is dropped from the local doc
          // and its stored state so it is not resent on every reconnect.
          if (code === 'read_only' && typeof msg.docId === 'string') {
            void system.registry.discardLocalChanges(msg.docId).catch((err: unknown) => {
              Sentry.captureException(err);
              console.error('[SyncSystemProvider] discarding a refused change failed:', err);
            });
            break;
          }

          // Spontaneous error (no matching correlation) — generic handling.
          if ((code === 'forbidden' || code === 'permission_denied') && msg.entityId) {
            const entityId = msg.entityId as string;
            fireDocAccessDenied(entityId, code as AccessDeniedReason);
            system.registry.unsubscribe(entityId);
          } else {
            Sentry.captureMessage(`[SyncSystemProvider] Server error: ${code}`, 'error');
            console.error('[SyncSystemProvider] Server error:', code, message);
          }
          break;
        }

        default:
          if (!isEngineHandledKind(envelope.kind)) {
            console.warn(`[SyncSystemProvider] unhandled kind: ${kindName(envelope.kind)}`);
          }
          break;
      }
    });
    return unsub;
  }, [system]);

  // Dispose on unmount (or when the system identity changes). The cleanup nulls
  // the ref so that if React re-mounts this component (StrictMode double-invoke,
  // or any remount), the next render's recreate-if-disposed check builds a fresh
  // live system instead of reusing the one we just disposed.
  useEffect(() => {
    return () => {
      void system.dispose();
      if (systemRef.current === system) {
        systemRef.current = null;
        // Force a re-render so the render-phase recreate builds a fresh system.
        // No-op if the component is truly unmounting (React drops the update);
        // essential when StrictMode/remount re-mounts us.
        forceRecreate((n) => n + 1);
      }
    };
  }, [system]);

  return (
    <SyncModeCtx.Provider value={mode}>
      <SyncSystemCtx.Provider value={system}>
        {children}
      </SyncSystemCtx.Provider>
    </SyncModeCtx.Provider>
  );
}

// HMR: this module owns React Contexts. Replacing it in place breaks
// every consumer registered against the old context identity (they read
// null and the `useSyncSystem` guard throws). Force a full page reload
// when this module updates so context identity stays consistent.
if (import.meta.hot) {
  import.meta.hot.invalidate();
}
